import React from "react";
import { Link, Navigate, Outlet, useNavigate } from "react-router-dom";
import { ExternalLink, Gem, LayoutDashboard, LogOut, ShieldAlert } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { brand } from "@/config/brand";

function NotAuthorised({ email, onSignOut }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-4" data-testid="admin-not-authorised">
      <div className="max-w-sm text-center text-ivory">
        <ShieldAlert size={36} className="mx-auto text-gold" />
        <h1 className="mt-6 font-serif text-3xl font-light">Not authorised</h1>
        <p className="mt-3 text-sm text-ivory/60">
          <span className="text-ivory">{email}</span> is a customer account. Sign in with an ÉLORA JEWELS staff account to open the admin console.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button type="button" onClick={onSignOut} data-testid="admin-switch-account" className="bg-gold px-6 py-3 text-[11px] uppercase tracking-[0.2em] text-ink hover:bg-ivory">Switch account</button>
          <Link to="/" className="border border-ivory/20 px-6 py-3 text-[11px] uppercase tracking-[0.2em] text-ivory hover:border-gold hover:text-gold">Back to store</Link>
        </div>
      </div>
    </div>
  );
}

function AdminTopBar({ email, onSignOut }) {
  return (
    <header className="border-b border-ivory/10 bg-ink text-ivory" data-testid="admin-topbar">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6 lg:px-8">
        <Link to="/admin" className="flex items-center gap-3" data-testid="admin-brand">
          <span className="grid h-9 w-9 place-items-center border border-gold/40 text-gold"><Gem size={16} /></span>
          <span>
            <span className="block font-serif text-lg leading-none tracking-[0.18em]">{brand.name}</span>
            <span className="block text-[9px] uppercase tracking-[0.3em] text-gold">Admin Console</span>
          </span>
        </Link>
        <nav className="flex items-center gap-1 text-[11px] uppercase tracking-widest">
          <Link to="/admin" className="inline-flex items-center gap-2 px-3 py-2 text-gold" data-testid="admin-nav-dashboard"><LayoutDashboard size={14} /> Dashboard</Link>
          <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-2 text-ivory/70 hover:text-ivory" data-testid="admin-nav-storefront"><ExternalLink size={14} /> Storefront</a>
        </nav>
        <div className="flex items-center gap-3 text-xs">
          <span className="hidden text-ivory/60 sm:inline" data-testid="admin-user-email">{email}</span>
          <button type="button" onClick={onSignOut} data-testid="admin-logout-button" className="inline-flex items-center gap-2 border border-ivory/20 px-3 py-2 text-[11px] uppercase tracking-widest text-ivory transition-colors hover:border-gold hover:text-gold">
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </div>
    </header>
  );
}

export function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (user === null) {
    return <div className="flex min-h-screen items-center justify-center bg-ink text-sm text-ivory/60" data-testid="admin-loading">Checking your session…</div>;
  }
  if (user === false) return <Navigate to="/admin/login" replace />;

  const signOut = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  if (user.role !== "admin") return <NotAuthorised email={user.email} onSignOut={signOut} />;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-layout">
      <AdminTopBar email={user.email} onSignOut={signOut} />
      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
        <Outlet />
      </main>
    </div>
  );
}
